/**
 * Field utilities for Filevine collection data.
 *
 * Consolidates field manipulation logic that was previously duplicated in:
 * - shared/FilevineEntityDetails.tsx
 * - shared/FilevineEntityPreview.tsx
 * - CollectionItemsTable.tsx
 */

import type {
  DetailedFilevineCollectionItemSchema,
  FilevineSectionFieldConfigSchema,
} from '@/services/api/generated/entitySyncService';

/**
 * Field types to exclude from display (doc gen buttons)
 */
export const EXCLUDED_FIELD_TYPES = {
  DOC_GEN_BUTTON: 29,
  DOC_GEN_AI_BUTTON: 1100,
} as const;

const EXCLUDED_FIELD_TYPE_VALUES: readonly number[] =
  Object.values(EXCLUDED_FIELD_TYPES);

/**
 * Get the value for a field from an item's snapshot data.
 *
 * Handles multiple ways field names can be stored:
 * 1. Direct internal_name access
 * 2. field_selector parsing (for paths like "section/fieldName")
 * 3. Case-insensitive search as fallback
 *
 * @param item - The Filevine collection item (optional for CREATE scenarios)
 * @param fieldConfig - The field configuration
 * @returns The field value or null if not found
 */
export function getFieldValue(
  item: DetailedFilevineCollectionItemSchema | undefined,
  fieldConfig: FilevineSectionFieldConfigSchema,
): unknown {
  if (!item?.latest_snapshot?.data) return null;

  const { data } = item.latest_snapshot;

  // Try direct internal_name access
  if (fieldConfig.internal_name in data) {
    return data[fieldConfig.internal_name];
  }

  // Try field_selector (might have path like "custom/fieldName")
  const selectorParts = fieldConfig.field_selector.split('/');
  const fieldName = selectorParts[selectorParts.length - 1];
  if (fieldName in data) {
    return data[fieldName];
  }

  // Case-insensitive search as fallback
  const lowerInternalName = fieldConfig.internal_name.toLowerCase();
  const matchingEntry = Object.entries(data).find(
    ([key]) => key.toLowerCase() === lowerInternalName,
  );
  if (matchingEntry) {
    return matchingEntry[1];
  }

  return null;
}

/**
 * Sort fields by row and column from field config data.
 *
 * Fields without position data are sorted to the end.
 *
 * @param fields - Array of field configs to sort
 * @returns New sorted array (original not mutated)
 */
export function sortFieldsByPosition(
  fields: FilevineSectionFieldConfigSchema[],
): FilevineSectionFieldConfigSchema[] {
  return [...fields].sort((a, b) => {
    const rowA = a.data?.row ?? Number.MAX_SAFE_INTEGER;
    const rowB = b.data?.row ?? Number.MAX_SAFE_INTEGER;
    if (rowA !== rowB) return rowA - rowB;

    const colA = a.data?.column ?? Number.MAX_SAFE_INTEGER;
    const colB = b.data?.column ?? Number.MAX_SAFE_INTEGER;
    return colA - colB;
  });
}

/**
 * Filter field configs to only displayable fields.
 *
 * Excludes:
 * - Obsolete fields
 * - Doc gen button fields (types 29 and 1100)
 *
 * @param fieldConfigs - Array of field configs to filter
 * @returns Filtered array of displayable fields
 */
export function getDisplayableFields(
  fieldConfigs: FilevineSectionFieldConfigSchema[],
): FilevineSectionFieldConfigSchema[] {
  return fieldConfigs.filter(
    (fc) =>
      !fc.is_obsolete &&
      !EXCLUDED_FIELD_TYPE_VALUES.includes(fc.custom_field_type),
  );
}

/**
 * Get displayable fields sorted by position.
 *
 * Convenience function combining getDisplayableFields and sortFieldsByPosition.
 *
 * @param fieldConfigs - Array of field configs
 * @returns Filtered and sorted array
 */
export function getDisplayableFieldsSorted(
  fieldConfigs: FilevineSectionFieldConfigSchema[],
): FilevineSectionFieldConfigSchema[] {
  return sortFieldsByPosition(getDisplayableFields(fieldConfigs));
}
